import React, { useEffect, useState } from "react";
import { RefreshCcw, Inbox, MessageCircle, Phone, Mail, CheckCircle2, Clock, Trash2, User, Users, Calendar, Compass, StickyNote, Filter } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";

const STATUS_META = {
    new: { label: "Yeni", cls: "bg-emerald-700/40 text-emerald-200 border-emerald-500/60" },
    contacted: { label: "İletişime Geçildi", cls: "bg-yellow-700/30 text-yellow-200 border-yellow-500/60" },
    handled: { label: "Tamamlandı", cls: "bg-blue-700/30 text-blue-200 border-blue-500/60" },
    closed: { label: "Kapatıldı", cls: "bg-emerald_deep text-muted_green border-border_green" },
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; }
};

const AdminGuideRequests = () => {
    const { settings } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/guide-requests");
            setItems(data);
        } catch { toast.error("Talepler yüklenemedi"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/guide-requests/${id}`, { status });
            setItems((cur) => cur.map((r) => (r.id === id ? { ...r, status } : r)));
            toast.success("Durum güncellendi");
        } catch { toast.error("Güncellenemedi"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Bu talep silinsin mi?")) return;
        try {
            await api.delete(`/admin/guide-requests/${id}`);
            setItems((cur) => cur.filter((r) => r.id !== id));
            toast.success("Silindi");
        } catch { toast.error("Silinemedi"); }
    };

    const filtered = statusFilter === "all" ? items : items.filter((r) => r.status === statusFilter);

    const counts = {
        all: items.length,
        new: items.filter((r) => r.status === "new").length,
        contacted: items.filter((r) => r.status === "contacted").length,
        handled: items.filter((r) => r.status === "handled").length,
        closed: items.filter((r) => r.status === "closed").length,
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Rehberlik Talepleri</h1>
                    <p className="text-muted_green text-sm">Kullanıcıların gönderdiği rehberlik hizmet taleplerini buradan yönetin.</p>
                </div>
                <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-requests"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
            </div>

            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
                <Filter className="w-4 h-4 text-muted_green flex-shrink-0" />
                {[
                    { k: "all", label: "Tümü" },
                    { k: "new", label: "Yeni" },
                    { k: "contacted", label: "İletişime Geçildi" },
                    { k: "handled", label: "Tamamlandı" },
                    { k: "closed", label: "Kapatıldı" },
                ].map((f) => (
                    <button
                        key={f.k}
                        onClick={() => setStatusFilter(f.k)}
                        className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border whitespace-nowrap transition-colors ${
                            statusFilter === f.k
                                ? "bg-gold text-emerald_deep border-gold"
                                : "border-border_green text-muted_green hover:border-gold hover:text-gold"
                        }`}
                        data-testid={`filter-${f.k}`}
                    >
                        {f.label} <span className="opacity-70 ml-1">({counts[f.k]})</span>
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center">
                    <Inbox className="w-14 h-14 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green text-sm">Talep bulunmuyor.</div>
                </div>
            ) : (
                <div className="space-y-4">
                    {filtered.map((r) => {
                        const badge = STATUS_META[r.status] || STATUS_META.new;
                        const wa = (r.phone || "").replace(/\D/g, "");
                        const waHref = wa ? `https://wa.me/${wa}?text=${encodeURIComponent(`Merhaba ${r.full_name}, ${settings.company_name || "Asis Hotels"} olarak rehberlik talebiniz için iletişime geçiyoruz.`)}` : null;
                        return (
                            <div key={r.id} className="border border-border_green bg-emerald_surface p-5" data-testid={`gr-${r.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className={`px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase border ${badge.cls}`}>{badge.label}</span>
                                            <span className="text-[10px] text-muted_green flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(r.created_at)}</span>
                                        </div>
                                        <div className="font-serif text-xl text-bone flex items-center gap-2"><User className="w-4 h-4 text-gold" /> {r.full_name}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {waHref && (
                                            <a href={waHref} target="_blank" rel="noreferrer" className="btn-gold !text-xs" data-testid={`wa-${r.id}`}>
                                                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                            </a>
                                        )}
                                        <a href={`tel:${r.phone}`} className="btn-outline-gold !text-xs" data-testid={`call-${r.id}`}>
                                            <Phone className="w-3.5 h-3.5" /> Ara
                                        </a>
                                        <button onClick={() => remove(r.id)} className="text-rose-400 hover:text-rose-500 p-2" data-testid={`del-${r.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                    <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-gold" /> {r.phone}</div>
                                    {r.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 text-gold flex-shrink-0" /> <span className="truncate">{r.email}</span></div>}
                                    <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-gold" /> {r.group_size} kişi</div>
                                    <div className="flex items-center gap-2"><Compass className="w-3.5 h-3.5 text-gold" /> {r.trip_type || "Umre"}</div>
                                    {r.preferred_date && <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gold" /> {new Date(r.preferred_date).toLocaleDateString("tr-TR")}</div>}
                                    {r.duration_days > 0 && <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-gold" /> {r.duration_days} gün</div>}
                                </div>

                                {r.notes && (
                                    <div className="mt-4 flex items-start gap-2 text-sm text-bone/85 bg-emerald_deep/40 border-l-2 border-gold/60 p-3">
                                        <StickyNote className="w-3.5 h-3.5 text-gold flex-shrink-0 mt-0.5" />
                                        <div className="whitespace-pre-wrap">{r.notes}</div>
                                    </div>
                                )}

                                <div className="mt-4 flex items-center gap-2 flex-wrap border-t border-border_green pt-4">
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted_green mr-2">Durum:</span>
                                    {["new", "contacted", "handled", "closed"].map((s) => {
                                        const active = r.status === s;
                                        return (
                                            <button
                                                key={s}
                                                onClick={() => !active && updateStatus(r.id, s)}
                                                className={`text-[11px] uppercase tracking-[0.15em] px-2.5 py-1 border transition-colors ${
                                                    active
                                                        ? "bg-gold text-emerald_deep border-gold"
                                                        : "border-border_green text-muted_green hover:text-gold hover:border-gold"
                                                }`}
                                                data-testid={`status-${r.id}-${s}`}
                                            >
                                                {STATUS_META[s].label}
                                            </button>
                                        );
                                    })}
                                    {r.status !== "new" && r.status !== "closed" && (
                                        <button
                                            onClick={() => updateStatus(r.id, "handled")}
                                            className="ml-auto text-[11px] uppercase tracking-[0.15em] px-2.5 py-1 border border-emerald-500 text-emerald-300 hover:bg-emerald-700/30 inline-flex items-center gap-1"
                                            data-testid={`mark-done-${r.id}`}
                                        >
                                            <CheckCircle2 className="w-3 h-3" /> Tamamla
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AdminGuideRequests;
